import axios from 'axios'
import React, { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AddOutlined from '@mui/icons-material/AddOutlined';
import PaidOutlined from '@mui/icons-material/PaidOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import { DataTable, EmptyState, PageHeader, StatusChip, type DataTableColumn } from '@routes-plan/ui';
import StandardModal, { useModalLoading } from '../../components/modales/StandardModal';
import { LoanPayment } from '../../models/loan-payment';
import PaymentModal from './modal/PaymentModal';
import EditPaymentModal from './payment-corrections/EditPaymentModal';
import PermissionButton from '../../components/common/PermissionButton';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { formatOperationalDateOnly } from '../../utils/operationalDateTime';
import { formatCurrency } from '../../utils/format/currency';
import PaymentReceipt from './PaymentReceipt';
import { useT } from '../../i18n';
import { setPageTitle } from '../../utils/pageTitle';
import RowAction from './components/RowAction';
import { ScreenTourButton } from '../../components/help/ScreenHelp';
import { pageSx, sectionSx } from './components/layout';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

/** Dato del préstamo en la ficha: etiqueta arriba y valor abajo. */
const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
      {value === '' || value == null ? '—' : value}
    </Typography>
  </Box>
);

const LoanPayments: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {

  const t = useT();
  setPageTitle('payments.detailTitle');
  const { id } = useParams();
  const [loanPayments, setLoanPayments] = useState<LoanPayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);

  const [idNumber, setIdNumber] = useState('');
  const [fullName, setFullName] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [mainAddress, setMainAddress] = useState('');

  const [date, setDate] = useState('');
  const [value, setValue] = useState('');
  const [totalValue, setTotalValue] = useState('');
  const [installmentValue, setInstallmentValue] = useState('');
  const [termDays, setTermDays] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | undefined>(undefined);
  // Abonos: registrar uno nuevo (Abonos y préstamos) y corregir uno ya registrado (con aprobación).
  const {
    canPay,
    canAllowDiscount,
    canAllowManualDiscount,
    canAllowAutomaticDiscount,
    canPdf,
    authUserData,
  } = useResourcePermissions('/loans/payments');
  const { canUpdate: canCorrect } = useResourcePermissions('/loans/payment-corrections');
  const [reload, setReload] = useState(0);
  // El Cargando general queda detrás del modal: el recibo usa el del modal estándar.
  const invoiceLoading = useModalLoading();
  const [modalInvoiceOpen, setModalInvoiceOpen] = useState(false);

  const [invoicePayId, setInvoicePayId] = useState<number | null>(null);
  const handleOpenInvoiceModal = (payId?: number) => {
    setInvoicePayId(payId ?? null);
    setModalInvoiceOpen(true);
  };

  const handleCloseInvoiceModal = () => {
    setModalInvoiceOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`loans/${id}/payments`);
        setLoanPayments(data);
      } catch (error) {
        showAlert(t('payments.loadError'), 'error');
        console.error(error);
      } finally {
        setLoadingPayments(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reload]);


  useEffect(() => {
    (
      async () => {
        const { data } = await axios.get(`loans/${id}`);
        setIdNumber(data.client.idNumber);
        setFullName(data.client.fullName);
        setMobilePhone(data.client.mobilePhone);
        setMainAddress(data.client.mainAddress);
        setDate(data.date);
        setValue(data.value);
        setTotalValue(data.totalValue);
        setInstallmentValue(data.installmentValue);
        setTermDays(data.termDays);
      }
    )().catch(() => showAlert(t('payments.loanLoadError'), 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reload]);

  const [payOpen, setPayOpen] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);

  const handleOpenCorrection = (paymentId: string | undefined) => {
    setSelectedPaymentId(paymentId);
    setCorrectOpen(true);
  };

  const formatFecha = (dateString: any) => formatOperationalDateOnly(dateString);

  const rows = loanPayments.map((payment: LoanPayment) => ({
    ...payment,
    id: payment.id,
    value: payment.value,
    discount: payment.discountValue,
    paymentDate: formatFecha(payment.paymentDate),
    daysLate: payment.daysLate,
    balance: payment.balance,
    order: payment.order,
    location: payment.location,
    interest: payment.interest,
    state: payment.state,
  }));
  type PaymentRow = (typeof rows)[number];
  /** El API manda `state: false` en los abonos anulados (el modelo lo tipa como texto). */
  const isVoid = (row: PaymentRow) => (row.state as unknown) === false;

  const columns: DataTableColumn<PaymentRow>[] = [
    { id: 'paymentDate', header: t('payments.payDate'), value: (row) => row.paymentDate },
    { id: 'daysLate', header: t('payments.daysLate'), align: 'right', value: (row) => Number(row.daysLate ?? 0), hideBelow: 'sm' },
    { id: 'value', header: t('payments.paidValue'), align: 'right', value: (row) => Number(row.value ?? 0), render: (row) => formatCurrency(row.value) },
    { id: 'discount', header: t('payments.discountValue'), align: 'right', value: (row) => Number(row.discount ?? 0), render: (row) => formatCurrency(row.discount), hideBelow: 'md' },
    { id: 'balance', header: t('payments.pendingBalance'), align: 'right', value: (row) => Number(row.balance ?? 0), render: (row) => formatCurrency(row.balance) },
    {
      id: 'state',
      header: t('common.status'),
      value: (row) => (isVoid(row) ? t('payments.stateVoid') : t('payments.stateApplied')),
      render: (row) =>
        isVoid(row) ? (
          <StatusChip label={t('payments.stateVoid')} tone="danger" />
        ) : (
          <StatusChip label={t('payments.stateApplied')} tone="success" />
        ),
    },
  ];

  const summary: { label: string; value: React.ReactNode }[] = [
    { label: t('payments.clientName'), value: fullName },
    { label: t('payments.clientIdNumber'), value: idNumber },
    { label: t('common.cellphone'), value: mobilePhone },
    { label: t('common.address'), value: mainAddress },
    { label: t('payments.loanDate'), value: date },
    { label: t('payments.loanValue'), value: value === '' ? '' : formatCurrency(value) },
    { label: t('payments.loanTotal'), value: totalValue === '' ? '' : formatCurrency(totalValue) },
    { label: t('payments.dailyPayment'), value: installmentValue === '' ? '' : formatCurrency(installmentValue) },
    { label: t('payments.term'), value: termDays },
  ];

  return (
    <Box sx={pageSx}>
      <PageHeader
        title={t('payments.detailTitle')}
        description={fullName ? t('payments.detailDescription', { client: fullName }) : undefined}
        breadcrumbs={[
          { label: t('menu.loans.payments'), href: '#/loans/payments' },
          { label: t('payments.detailTitle') },
        ]}
        helpAction={<ScreenTourButton />}
        actions={
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <PermissionButton
              allowed={canPay}
              deniedTooltip={t('payments.noPermissionPay')}
              variant="contained"
              startIcon={<PaidOutlined />}
              onClick={() => setPayOpen(true)}
              data-tour="loan-detail-pay"
            >
              {t('payments.actionPay')}
            </PermissionButton>
            <Button variant="outlined" startIcon={<AddOutlined />} component={NavLink} to="/loans/new" data-tour="loan-detail-new-loan">
              {t('loans.newLoan')}
            </Button>
          </Box>
        }
      />
      <Paper variant="outlined" sx={sectionSx} data-tour="loan-detail-summary">
        <Typography variant="h6" gutterBottom>
          {t('payments.loanSection')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))',
            gap: 2,
          }}
        >
          {summary.map((item) => (
            <Field key={item.label} label={item.label} value={item.value} />
          ))}
        </Box>
      </Paper>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('payments.scheduleTitle')}
        </Typography>
        <DataTable<PaymentRow>
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          loading={loadingPayments}
          exportFileName={t('payments.exportFileName')}
          ariaLabel={t('payments.scheduleTitle')}
          tourId="loan-detail"
          rowActions={(row) => (
            <>
              {row.location ? (
                <RowAction
                  label={t('payments.location')}
                  icon={<PlaceOutlined fontSize="small" />}
                  href={`https://www.google.com/maps?q=${row.location}`}
                />
              ) : null}
              {!isVoid(row) ? (
                <RowAction
                  label={t('payments.receiptTitle')}
                  icon={<ReceiptLongOutlined fontSize="small" />}
                  onClick={() => handleOpenInvoiceModal(row.id as unknown as number)}
                />
              ) : null}
              {!isVoid(row) && canCorrect ? (
                <RowAction
                  label={t('payments.actionCorrect')}
                  icon={<EditOutlined fontSize="small" />}
                  onClick={() => handleOpenCorrection(row.id as unknown as string)}
                />
              ) : null}
            </>
          )}
          emptyState={<EmptyState dense icon={<ReceiptLongOutlined />} title={t('payments.emptyTitle')} />}
        />
      </Box>
      {/* Registrar abono: abono nuevo del préstamo, igual que en Abonos y préstamos. */}
      <PaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        client={null}
        clientId={id ? Number(id) : undefined}
        currentUserId={authUserData?.id}
        canAllowDiscount={canAllowDiscount}
        canAllowManualDiscount={canAllowManualDiscount}
        canAllowAutomaticDiscount={canAllowAutomaticDiscount}
        canPdf={canPdf}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
        onPaymentSuccess={() => setReload((n) => n + 1)}
      />
      {/* Corregir abono: el mismo flujo de Corregir abonos; si no es superadmin queda pendiente de aprobación. */}
      <EditPaymentModal
        isOpen={correctOpen}
        onClose={() => setCorrectOpen(false)}
        onButtonClick={() => {
          setCorrectOpen(false);
          setReload((n) => n + 1);
        }}
        id={selectedPaymentId ?? ''}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <StandardModal
        open={modalInvoiceOpen}
        onClose={handleCloseInvoiceModal}
        title={t('payments.receiptTitle')}
        maxWidth={480}
        loading={invoiceLoading.loading}
      >
        <PaymentReceipt payId={invoicePayId ?? undefined} showAlert={showAlert} showLoading={invoiceLoading.showLoading} hideLoading={invoiceLoading.hideLoading} />
      </StandardModal>
    </Box>
  );
}

export default LoanPayments
